import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { mockTopics, mockReplies, mockUsers, forumCategories } from '../data/forumData';
import { ThumbsUp } from 'lucide-react';
import SEOHead from "../components/SEOHead";

import { useTranslation } from 'react-i18next';
const REPLY_UPVOTES_KEY = 'maxemployee.forum.replyUpvotes';
const TOPIC_UPVOTES_KEY = 'maxemployee.forum.topicUpvotes';

function loadReplyUpvotes() {
try {
    const stored = localStorage.getItem(REPLY_UPVOTES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveReplyUpvotes(upvotes) {
  try {
    localStorage.setItem(REPLY_UPVOTES_KEY, JSON.stringify(upvotes));
  } catch {}
}

function loadTopicUpvotes() {
  try {
    const stored = localStorage.getItem(TOPIC_UPVOTES_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

function saveTopicUpvotes(upvotes) {
  try {
    localStorage.setItem(TOPIC_UPVOTES_KEY, JSON.stringify(upvotes));
  } catch {}
}

const ForumTopic = () => {
  const { t } = useTranslation();
  const { topicId } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [replies, setReplies] = useState([]);
  const [newReply, setNewReply] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [replyUpvotes, setReplyUpvotes] = useState({});
  const [topicUpvoted, setTopicUpvoted] = useState(false);

  useEffect(() => {
    const foundTopic = mockTopics.find(t => t.id === topicId);
    if (!foundTopic) {
      console.error('Topic not found:', topicId);
      navigate('/community');
      return;
    }
    setTopic(foundTopic);

    // Load stored upvotes into reply data
    const storedReplyVotes = loadReplyUpvotes();
    const storedTopicVotes = loadTopicUpvotes();
    setReplyUpvotes(storedReplyVotes);
    setTopicUpvoted(!!storedTopicVotes[topicId]);

    const topicReplies = mockReplies.filter(r => r.topicId === topicId).map(r => ({
      ...r,
      upvotes: r.upvotes + (storedReplyVotes[r.id] || 0),
    }));
    setReplies(topicReplies);

    setCurrentUser(mockUsers[0]);
  }, [topicId, navigate]);

  const handleReplySubmit = (e) => {
    e.preventDefault();
    if (!newReply.trim() || !currentUser) return;

    const reply = {
      id: `reply-${Date.now()}`,
      topicId: topicId,
      author: currentUser.id,
      avatar: currentUser.avatar,
      createdAt: new Date().toISOString(),
      content: newReply,
      upvotes: 0,
    };

    setReplies([reply, ...replies]);
    setNewReply('');
  };

  const handleReplyUpvote = (replyId) => {
    setReplyUpvotes(prev => {
      const current = prev[replyId] || 0;
      const next = { ...prev, [replyId]: current + 1 };
      saveReplyUpvotes(next);
      return next;
    });
    setReplies(currentReplies =>
      currentReplies.map(r =>
        r.id === replyId ? { ...r, upvotes: r.upvotes + 1 } : r
      )
    );
  };

  const handleTopicUpvote = () => {
    if (topicUpvoted) return;
    setTopicUpvoted(true);
    setTopicUpvotes(prev => {
      const next = { ...prev, [topicId]: true };
      saveTopicUpvotes(next);
      return next;
    });
  };

  const getCategoryName = (categoryId) => {
    const category = forumCategories.find(cat => cat.id === categoryId);
    return category ? category.name : 'General';
  };

  if (!topic) {
    return <div className="container mx-auto p-4 text-zinc-400">{t('common.loading')}</div>;
  }

  const topicAuthor = mockUsers.find(u => u.id === topic.author);

  return (
    <div className="container mx-auto p-4">
      <SEOHead
        title={t('forum_topic.community_discussion_max_employee')}
        description={t('forum_topic.join_the_max_employee_community_discuss_ai_agents')}
        url="/community"
      />
      <div className="text-sm breadcrumbs text-zinc-500 mb-4">
        <ul>
          <li><Link to="/community" className="hover:text-amber-500">{t('nav.community')}</Link></li>
          <li><Link to={`/community/${topic.categoryId}`} className="hover:text-amber-500">{getCategoryName(topic.categoryId)}</Link></li>
          <li>{topic.title}</li>
        </ul>
      </div>

      <h1 className="text-4xl font-bold mb-2 text-amber-500">{topic.title}</h1>
      <div className="text-zinc-400 text-sm flex items-center mb-6">
        <img src={topicAuthor?.avatar || '/path/to/default/avatar.png'} alt={t('forum_topic.author_avatar')} className="w-8 h-8 rounded-full mr-3" loading="lazy" decoding="async" />
        <span className="mr-4">{topicAuthor?.name || topic.author}</span>
        <span className="mr-4">&bull; {new Date(topic.createdAt).toLocaleDateString()}</span>
        <span className="mr-4">&bull; {topic.views} views</span>
        <span className="mr-4">&bull; {replies.length} replies</span>
        <button
          onClick={handleTopicUpvote}
          className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded transition-colors duration-200 ${
            topicUpvoted
              ? 'bg-amber-500/20 text-amber-500'
              : 'text-zinc-400 hover:text-amber-500 hover:bg-zinc-800'
          }`}
          title={topicUpvoted ? 'Upvoted!' : 'Upvote this topic'}
        >
          <ThumbsUp className={`w-4 h-4 ${topicUpvoted ? 'fill-amber-500' : ''}`} />
          <span className="text-xs font-medium">{t('forum_topic.upvote')}</span>
        </button>
        {topic.solved && <span className="ml-3 px-2 py-1 rounded-full bg-green-600 text-white text-xs">{t('forum.solved')}</span>}
      </div>

      {/* Topic Content */}
      <div className="bg-zinc-800 p-6 rounded shadow-sm mb-8">
        <p className="text-zinc-200 leading-relaxed">{topic.content}</p>
      </div>

      {/* Replies Section */}
      <h2 className="text-2xl font-semibold mb-4 text-zinc-300">Replies ({replies.length})</h2>
      <div className="space-y-6">
        {replies.map(reply => {
          const replyAuthor = mockUsers.find(u => u.id === reply.author);
          const isUpvoted = !!replyUpvotes[reply.id];
          return (
            <div key={reply.id} className="bg-zinc-800 p-5 rounded shadow-sm">
              <div className="flex items-center mb-3">
                <img src={replyAuthor?.avatar || '/path/to/default/avatar.png'} alt={t('forum_topic.reply_author_avatar')} className="w-8 h-8 rounded-full mr-3" loading="lazy" decoding="async" />
                <span className="font-semibold text-zinc-300 mr-2">{replyAuthor?.name || reply.author}</span>
                <span className="text-zinc-400 text-sm">&bull; {new Date(reply.createdAt).toLocaleDateString()}</span>
                <button
                  onClick={() => handleReplyUpvote(reply.id)}
                  className={`ml-auto inline-flex items-center gap-1 px-2 py-1 rounded transition-colors duration-200 ${
                    isUpvoted
                      ? 'text-amber-500'
                      : 'text-zinc-400 hover:text-amber-500 hover:bg-zinc-700'
                  }`}
                  title={isUpvoted ? 'Upvoted!' : 'Upvote this reply'}
                >
                  <ThumbsUp className={`w-3.5 h-3.5 ${isUpvoted ? 'fill-amber-500' : ''}`} />
                  <span className="text-xs font-medium">{reply.upvotes}</span>
                </button>
                {reply.isSolution && <span className="ml-3 px-2 py-1 rounded-full bg-green-600 text-white text-xs">{t('forum.solution')}</span>}
              </div>
              <p className="text-zinc-200 leading-relaxed ml-12 pl-3 border-l-2 border-zinc-700">{reply.content}</p>
            </div>
          );
        })}
      </div>

      {/* Reply Form */}
      <div className="mt-12 bg-zinc-800 p-6 rounded shadow-sm">
        <h3 className="text-2xl font-semibold mb-4 text-zinc-300">{t('forum_topic.leave_a_reply')}</h3>
        <form onSubmit={handleReplySubmit}>
          <textarea
            rows="5"
            placeholder={t('forum_topic.your_reply')}
            value={newReply}
            onChange={(e) => setNewReply(e.target.value)}
            className="w-full p-3 rounded bg-zinc-900 text-white border border-zinc-700 focus:outline-none focus:border-amber-500 mb-4"
            required
          ></textarea>
          <button type="submit" className="bg-amber-500 text-black px-6 py-2 rounded hover:bg-amber-400 transition-colors duration-200">{t('forum_topic.post_reply')}</button>
        </form>
      </div>
    </div>
  );
};

export default ForumTopic;
